import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Building2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { getAgentLoanById, resubmitAgentLoanDocument, uploadAgentLoanDocument } from '../../lib/api';

const statusColors = {
  'Under Processing': 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  'Resubmission Required': 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
  Approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  Disbursed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  Rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  Hold: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
};

const docLabelMap = {
  panCard: 'PAN Card',
  aadhaarCard: 'Aadhaar Card',
  salarySlips: 'Salary Slips',
  bankStatement: 'Bank Statement',
  propertyDocs: 'Property Documents',
  photograph: 'Photograph',
  gstCert: 'GST Certificate',
  itr: 'ITR',
  bizProof: 'Business Registration Proof',
  dealerQuote: 'Dealer Quotation',
  studentId: 'Student ID / Marksheet',
  admission: 'Admission Letter',
  feeStructure: 'Fee Structure',
  incomeProof: 'Income Proof',
};

const expectedDocKeysByLoanType = {
  home: ['panCard', 'aadhaarCard', 'salarySlips', 'bankStatement', 'propertyDocs', 'photograph'],
  business: ['panCard', 'aadhaarCard', 'gstCert', 'itr', 'bankStatement', 'bizProof'],
  car: ['panCard', 'aadhaarCard', 'salarySlips', 'bankStatement', 'dealerQuote'],
  personal: ['panCard', 'aadhaarCard', 'salarySlips', 'bankStatement', 'photograph'],
  education: ['studentId', 'admission', 'feeStructure', 'panCard', 'aadhaarCard', 'incomeProof', 'bankStatement'],
};

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const prettyStage = (stage) => {
  if (!stage) return '-';
  return stage
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const isImage = (name) => /\.(jpe?g|png|gif|webp|svg)$/i.test(name || '');
const isPdf = (name) => /\.pdf$/i.test(name || '');

function FilePreview({ fileUrl, fileName, label }) {
  if (!fileUrl) {
    return <span className="text-xs text-muted-foreground italic">Not uploaded</span>;
  }
  if (isImage(fileName)) {
    return (
      <a href={fileUrl} target="_blank" rel="noreferrer" title="Open document">
        <img
          src={fileUrl}
          alt={label}
          className="h-24 w-auto max-w-[180px] rounded-lg border border-border object-cover shadow-sm hover:opacity-90 transition-opacity cursor-pointer"
        />
      </a>
    );
  }
  if (isPdf(fileName)) {
    return (
      <a
        href={fileUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2 text-xs font-medium hover:bg-accent"
      >
        View PDF
      </a>
    );
  }
  return (
    <a href={fileUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
      {fileName || 'View file'}
    </a>
  );
}

export default function AgentLoanDetails() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id } = useParams();
  const { token } = useAuth();

  const [loan, setLoan] = useState(state?.loan || null);
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState({});
  const [savingKey, setSavingKey] = useState('');

  const fetchLoan = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const resp = await getAgentLoanById(token, id);
      setLoan(resp?.data || null);
    } catch (err) {
      toast.error(err?.message || 'Failed to load loan details');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchLoan();
  }, [fetchLoan]);

  const details = loan?.product_details || loan?.productDetails || {};
  const documents = useMemo(() => {
    if (Array.isArray(loan?.documents)) return loan.documents;
    if (Array.isArray(details.documents)) return details.documents;
    return [];
  }, [details.documents, loan?.documents]);

  const docsByKey = useMemo(() => {
    return documents.reduce((acc, doc) => {
      if (doc?.key) acc[doc.key] = doc;
      return acc;
    }, {});
  }, [documents]);

  const rejectedDocuments = useMemo(
    () => documents.filter((doc) => doc.current_status === 'rejected'),
    [documents],
  );
  const isCorrectionMode = loan?.status === 'Resubmission Required';

  const handleFileChange = (key, file) => {
    setPendingFiles((prev) => {
      const next = { ...prev };
      if (file) next[key] = file;
      else delete next[key];
      return next;
    });
  };

  const handleUpload = async (docKey, documentId) => {
    if (!token || !loan?.id) return;
    const file = pendingFiles[docKey];
    if (!file) {
      toast.error('Please choose a file first.');
      return;
    }
    setSavingKey(docKey);
    try {
      if (documentId) {
        await resubmitAgentLoanDocument(token, loan.id, documentId, file);
        toast.success('Document resubmitted for review.');
      } else {
        await uploadAgentLoanDocument(token, loan.id, docKey, file);
        toast.success('Document uploaded successfully.');
      }
      setPendingFiles((prev) => {
        const next = { ...prev };
        delete next[docKey];
        return next;
      });
      await fetchLoan();
    } catch (err) {
      toast.error(err?.message || 'Failed to upload document');
    } finally {
      setSavingKey('');
    }
  };

  if (!loan) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" className="mb-2 gap-2" onClick={() => navigate('/agent/loans')}>
          <ArrowLeft className="h-4 w-4" />
          Back to Loan Applications
        </Button>
        <Card className="border border-border">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading loan details…' : `Loan details not found for ID ${id}.`}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const expectedDocKeys = expectedDocKeysByLoanType[loan.loan_type] || [];

  return (
    <div className="space-y-4">
      <Button variant="ghost" className="mb-2 gap-2" onClick={() => navigate('/agent/loans')}>
        <ArrowLeft className="h-4 w-4" />
        Back to Loan Applications
      </Button>

      {loan.status === 'Resubmission Required' ? (
        <Card className="border-rose-300 bg-rose-50/70 dark:bg-rose-950/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-100 p-2 dark:bg-rose-900/40">
                <AlertTriangle className="h-5 w-5 text-rose-600" />
              </div>
              <div className="space-y-1">
                <h2 className="text-base font-semibold">Resubmission Required</h2>
                <p className="text-sm text-muted-foreground">
                  This application needs updated documents before it can proceed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
      {loan.status === 'Resubmission Required' && rejectedDocuments.length === 0 ? (
        <Card className="border-amber-200 bg-amber-50/70 dark:bg-amber-950/20">
          <CardContent className="pt-5 text-sm text-amber-700">
            No rejected documents are recorded yet. Ask the reviewer to reject specific documents so you can resubmit them.
          </CardContent>
        </Card>
      ) : null}

      <Card className="border border-border">
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center text-white font-bold text-lg">
              {loan.applicant_name?.split(' ').map((n) => n[0]).join('').slice(0, 2)}
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold font-['Plus_Jakarta_Sans']">
                {loan.applicant_name}
              </h1>
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-mono">{loan.id}</span>
                <span>•</span>
                <Badge className={statusColors[loan.status] || ''}>{loan.status}</Badge>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Loan Type</p>
              <p className="text-sm font-semibold">{loan.loan_type}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Bank / NBFC</p>
              <p className="text-sm font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                {loan.bank_name || '-'}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Loan Amount</p>
              <p className="text-sm font-semibold text-emerald-600">{fmt(loan.loan_amount)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Stage</p>
              <p className="text-sm font-semibold text-muted-foreground">{prettyStage(loan.stage)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Applied Date</p>
              <p className="text-sm font-semibold">{loan.created_at || '-'}</p>
            </div>
          </div>

          {rejectedDocuments.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Rejected Documents
              </p>
              <div className="space-y-3">
                {rejectedDocuments.map((doc) => {
                  const pendingFile = pendingFiles[doc.key];
                  return (
                    <div key={doc.id} className="rounded-xl border border-rose-200 bg-rose-50/60 dark:bg-rose-950/20 p-4 space-y-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-1">
                          <p className="font-semibold">{doc.document_type || doc.document_label || docLabelMap[doc.key] || doc.key}</p>
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span>Status: {doc.current_status}</span>
                            <span>Reason code: {doc.rejection_reason_code || '-'}</span>
                          </div>
                        </div>
                        {doc.file_url ? <FilePreview fileUrl={doc.file_url} fileName={doc.file_name} label={doc.document_type} /> : null}
                      </div>
                      <div className="rounded-lg border bg-background px-3 py-2 text-xs space-y-1">
                        <p><span className="font-medium">Reason for rejection:</span> {doc.rejection_reason_code || '-'}</p>
                        <p><span className="font-medium">Comment:</span> {doc.rejection_note || '-'}</p>
                        <p><span className="font-medium">Reviewer:</span> {doc.reviewer_name || '-'}
                        {/* {doc.reviewer_role ? ` (${doc.reviewer_role})` : ''} */}
                        </p>
                        <p><span className="font-medium">Rejected At:</span> {doc.reviewed_at || '-'}</p>
                      </div>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <input type="file" className="text-xs" onChange={(e) => handleFileChange(doc.key, e.target.files?.[0] || null)} />
                        <Button
                          className="w-full sm:w-auto"
                          disabled={!pendingFile || savingKey === doc.key}
                          onClick={() => handleUpload(doc.key, doc.id)}
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          {savingKey === doc.key ? 'Uploading…' : 'Resubmit document'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Uploaded Documents
            </p>
            {isCorrectionMode ? (
              <div className="rounded-lg border border-green-200 bg-green-50/60 px-3 py-2 text-xs text-green-700">
                Uploads are locked while this application is in correction mode. Please update only the rejected documents above.
              </div>
            ) : null}
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {expectedDocKeys.map((key) => {
                    const doc = docsByKey[key];
                    const pendingFile = pendingFiles[key];
                    const allowUpload = !isCorrectionMode && (!doc || !doc.file_name);
                    return (
                      <tr key={key} className="border-b last:border-0 align-top">
                        <td className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300 w-1/3">
                          {docLabelMap[key] || key}
                        </td>
                        <td className="px-4 py-3 space-y-2">
                          <FilePreview fileUrl={doc?.file_url} fileName={doc?.file_name} label={docLabelMap[key] || key} />
                          <div className="text-xs text-muted-foreground">
                            {doc ? `Status: ${doc.current_status}` : 'Not uploaded'}
                          </div>
                          {allowUpload ? (
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                              <input type="file" className="text-xs" onChange={(e) => handleFileChange(key, e.target.files?.[0] || null)} />
                              <Button
                                size="sm"
                                disabled={!pendingFile || savingKey === key}
                                onClick={() => handleUpload(key, null)}
                              >
                                {savingKey === key ? 'Uploading…' : 'Upload document'}
                              </Button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
